import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Icon,
  Input,
  Link,
  Spinner,
  Text,
} from '@nimbus-ds/components';
import {
  ChatDotsIcon,
  ClockIcon,
  ExternalLinkIcon,
  PlayIcon,
  TagIcon,
  WhatsappIcon,
} from '@nimbus-ds/icons';
import { HelpLink, InitialScreen } from '@nimbus-ds/patterns';
import { Trans, useTranslation } from 'react-i18next';
import { NUVEM_CHAT_WAITLIST } from 'App/featuresFlags';
import { useToast, useTranslationLanguage } from 'commons/hooks';
import { openWindow } from 'commons/utils/window';
import { useGetTags } from 'domains/Auth/hooks';
import useRefreshInfoStoreUser from 'domains/Auth/hooks/useRefreshInfoToSync';
import NuvemChatHeroImage from './images/waitlist-image.png';
import NuvemChatSuccessImage from './images/waitlist-success-image.png';
import { useAddToWaitListMutation } from '../nuvemChatApi/nuvemChatApi';
import { trackingNuvemChatWaitListClick } from '../tracking';

export function NuvemChatWaitListPage() {
  const { t } = useTranslation('nuvemchat');
  const language = useTranslationLanguage();
  const [addToWaitList, { isLoading }] = useAddToWaitListMutation();
  const [email, setEmail] = useState('');
  const tags = useGetTags();
  const { refreshInfo } = useRefreshInfoStoreUser();
  const isInWaitList = tags.includes(NUVEM_CHAT_WAITLIST);
  const { addToast } = useToast();
  const linksInfoChat = {
    'es-AR': 'https://www.tiendanube.com/soluciones/chat-nube',
    'es-MX': 'https://www.tiendanube.com/mx/soluciones/chat-nube',
    'es-CO': 'https://www.tiendanube.com/co/soluciones/chat-nube',
    'es-CL': 'https://www.tiendanube.com/cl/soluciones/chat-nube',
    'pt-BR': 'https://www.nuvemshop.com.br/solucoes/nuvem-chat',
  };
  const linkVideo =
    language === 'pt-BR'
      ? 'https://www.youtube.com/watch?v=4m0rSPrfCRE'
      : 'https://www.youtube.com/watch?v=TVp2daz8pgI';
  const isValidEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddToWaitListClick = async (email: string) => {
    try {
      await addToWaitList().unwrap();
      addToast({
        label: t('waitList.success'),
        appearance: 'success',
      });
      refreshInfo(true);
      trackingNuvemChatWaitListClick(email);
    } catch (error) {
      addToast({
        label: t('waitList.error'),
        appearance: 'danger',
      });
    }
  };
  const renderWaitListForm = () => (
    <Box display="flex" flexDirection="column" gap="2" width="80%">
      <Text fontSize="highlight" color="neutral-textHigh" textAlign="left">
        <Trans
          t={t}
          i18nKey="waitList.hero.description"
          components={{ bold: <strong /> }}
        />
      </Text>

      <Card>
        <Box display="flex" flexDirection="row" gap="2">
          <Input
            placeholder={t('waitList.hero.inputPlaceholder')}
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isValidEmail) {
                handleAddToWaitListClick(email);
              }
            }}
          />
          <Button
            appearance="primary"
            onClick={() => handleAddToWaitListClick(email)}
            disabled={!isValidEmail || isLoading}
          >
            {isLoading && <Spinner color="neutral-interactive" size="small" />}
            {t('waitList.hero.button')}
          </Button>
        </Box>
      </Card>
    </Box>
  );
  const renderWaitList = () => (
    <InitialScreen.Hero
      subtitle="CHAT"
      title={t('waitList.hero.title')}
      bullets={[
        <InitialScreen.Bullet
          key="bullet1"
          icon={<WhatsappIcon />}
          text={t('waitList.hero.bullet1')}
        />,
        <InitialScreen.Bullet
          key="bullet2"
          icon={<ClockIcon />}
          text={t('waitList.hero.bullet2')}
        />,
        <InitialScreen.Bullet
          key="bullet3"
          icon={<TagIcon />}
          text={t('waitList.hero.bullet3')}
        />,
        <InitialScreen.Bullet
          key="bullet4"
          icon={<ChatDotsIcon />}
          text={t('waitList.hero.bullet4')}
        />,
      ]}
      actions={renderWaitListForm()}
      image={
        <img
          src={NuvemChatHeroImage}
          alt="NuvemChat"
          width="100%"
          height="100%"
        />
      }
    />
  );

  const renderSubscribedWaitList = () => (
    <InitialScreen.Hero
      title={t('waitList.subscribed.title')}
      description={t('waitList.subscribed.description')}
      bullets={[
        <InitialScreen.Bullet
          key="bullet1"
          icon={<PlayIcon />}
          text={t('waitList.subscribed.bullet2')}
        />,
      ]}
      actions={
        <Button
          appearance="primary"
          onClick={() => openWindow(linkVideo, true)}
        >
          {t('waitList.subscribed.button2')}
        </Button>
      }
      image={
        <img
          src={NuvemChatSuccessImage}
          alt="NuvemChat"
          width="100%"
          height="100%"
        />
      }
    />
  );
  return (
    <InitialScreen>
      {isInWaitList ? renderSubscribedWaitList() : renderWaitList()}

      <Box
        display="flex"
        flexDirection="row"
        gap="2"
        justifyContent="center"
        mt="5"
      >
        <HelpLink>
          <Link
            as="a"
            href={linksInfoChat[language]}
            target="_blank"
            appearance="primary"
            textDecoration="none"
          >
            {t('waitList.helpText')}
            <Icon source={<ExternalLinkIcon />} color="currentColor" />
          </Link>
        </HelpLink>
      </Box>
    </InitialScreen>
  );
}
